import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from '@qodo/design-system';
import { useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { v4 as uuid4 } from 'uuid';
import '../css/input.css';
import { ConsentBanner } from '../components/ConsentBanner';
import { Loader } from '../components/Loader';
import { SESSION_STORAGE_KEYS } from '../consts/sessionStorage';
import { useAccountInfo } from '../hooks/billing/useAccountInfo';
import { PageViewTracker } from '../hooks/usePageViewTracking';
import {
  addAuthorizationHeader,
  createRefreshTokenErrorInterceptor,
  REDIRECT_IN_PROGRESS_ERROR_NAME,
} from '../network/httpClients/interceptors';
import { QUERY_PARAMS } from '../routes/consts';
import { Routes as V2Routes } from '../routes/Routes';
import { mixpanelService } from '../services/mixpanel';
import { getDynamicSetting } from '../utils/dynamicSettings';
import { storeIdInSessionStorage } from '../utils/url';
import { AuthContext } from './auth';
import { zendeskService } from './support/zendesk';

export default function App() {
  const { user, isInitialized: isAuthInitialized } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);

  const { isLoading: isAccountInfoLoading, error: accountInfoError } =
    useAccountInfo({
      enabled: isInitialized && !!user,
    });

  // account-info is the first verify_user_token-gated call, so it is where a dedicated-org user
  // gets their MT→ST 409. By the time it settles the interceptor has already handed the tokens
  // to the shared-domain cookie and started navigating. Mounting the routes now would fire their
  // queries into that window, and a 401 among them signs the user out — revoking the grant and
  // deleting the cookie the destination portal is about to read (the MT↔ST bounce loop). Hold
  // the Loader until the navigation lands.
  const isTenantRedirectInFlight =
    accountInfoError?.name === REDIRECT_IN_PROGRESS_ERROR_NAME;

  const initializeServicesIfNeeded = (): void => {
    // The reason that we are not calling mixpanelService.identify here is because we want to identify the user only after the user is fetched from the server, because we need the userId.
    // Hence, we are calling mixpanelService.init here, and then calling mixpanelService.identify in the Routes.tsx file.
    mixpanelService.init(queryClient);

    if (isInitialized && user) {
      zendeskService.init(user);
      zendeskService.identify(user);
    }
  };

  useEffect(() => {
    initializeServicesIfNeeded();
  }, [isInitialized, user]);

  useEffect(() => {
    if (!isAuthInitialized) return;

    storeIdInSessionStorage(
      QUERY_PARAMS.SESSION_ID,
      SESSION_STORAGE_KEYS.SESSION_ID,
      false,
    );

    storeIdInSessionStorage(
      QUERY_PARAMS.TRACE_ID,
      SESSION_STORAGE_KEYS.TRACE_ID,
    );

    storeIdInSessionStorage(
      QUERY_PARAMS.INIT_CLIENT,
      SESSION_STORAGE_KEYS.INIT_CLIENT,
      false,
    );

    Axios.defaults.baseURL = getDynamicSetting('server_url');
    Axios.interceptors.request.use(
      async (config) => {
        config.headers['Request-id'] = uuid4();
        config.headers['Qodo-Client-Type'] = 'portal';
        config.headers['Access-Control-Allow-Origin'] = '*';

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Request interceptor to add the Authorization header from localstorage, if it exists
    Axios.interceptors.request.use(addAuthorizationHeader);

    // Add response interceptor for 401 and 403 handling and automatic token refresh
    Axios.interceptors.response.use(
      (response) => response,
      createRefreshTokenErrorInterceptor(Axios),
    );

    setIsInitialized(true);
  }, [isAuthInitialized]);

  return (
    <>
      <Toaster />
      <ConsentBanner />

      <BrowserRouter>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
          <PageViewTracker />
          {isInitialized &&
          !isAccountInfoLoading &&
          !isTenantRedirectInFlight ? (
            <V2Routes />
          ) : (
            <Loader center />
          )}
        </QueryParamProvider>
      </BrowserRouter>
    </>
  );
}
